import { create, UseBoundStore } from 'zustand';

import _ from 'scbk-services/utils/lodash-extension.ts';

import type { SecureContext } from 'scbk-services/types/shared-context/secure-context';
import type { StoreApi } from 'zustand';

interface secureContextStore {
    context: SecureContext.Context;
    getSecureContext: () => SecureContext.Context;
    resetSecureContext: () => void;
    setSignContext: (sign: SecureContext.SignContext) => void;
    setTokensContext: (sign: SecureContext.TokensContext) => void;
    resetOthersContext: () => void;
    setKeypadContext: (keypad: SecureContext.KeypadContext) => void;
    resetKeypadContext: () => void;
    setSubKeypadContext: (keypad: SecureContext.SubKeypadContext) => void;
    resetSubKeypadContext: () => void;
    resetKeypadPlainMapSecureContext: () => void;
}

let storeInstance: UseBoundStore<StoreApi<secureContextStore>> | null = null;

/**
 * 객체 내부의 undefined 값을 재귀적으로 제거한다.
 * @param obj 임의의 객체 / 배열
 * @returns undefined 값이 제거된 새로운 객체
 */
const omitDeepUndefined = <T>(obj: T): T => {
    if (_.isArray(obj)) {
        return obj.map(omitDeepUndefined) as any;
    }

    if (!_.isPlainObject(obj)) {
        return obj;
    }

    const output: any = {};
    Object.entries(obj as any).forEach(([k, v]) => {
        if (_.isUndefined(v)) {
            return;
        }

        const next = omitDeepUndefined(v);
        output[k] = next;
    });
    return output;
};

const createStore = () =>
    create<secureContextStore>((set, get) => ({
        context: {},
        getSecureContext: () => get().context,
        resetSecureContext: () => set({ context: {} }),
        setSignContext: (sign: SecureContext.SignContext) =>
            set((state) => ({ context: { ...state.context, sign: sign } })),
        setTokensContext: (tokens: SecureContext.TokensContext) =>
            set((state) => ({ context: { ...state.context, tokens: tokens } })),
        resetOthersContext: () =>
            set((state) => ({
                context: omitDeepUndefined({
                    keypad: { ...state.context.keypad },
                    subKeypad: { ...state.context.subKeypad },
                }),
            })), // 키패드를 제외한 Context
        setKeypadContext: (keypad: SecureContext.KeypadContext) =>
            set((state) => ({ context: { ...state.context, keypad: keypad } })),
        resetKeypadContext: () =>
            set((state) => {
                console.log(
                    '### state.context.keypad?.extE2EPlainMap : ',
                    state.context.keypad?.extE2EPlainMap,
                );
                return {
                    context: omitDeepUndefined({
                        ...state.context,
                        keypad: { extE2EPlainMap: state.context.keypad?.extE2EPlainMap },
                    }),
                };
            }),
        setSubKeypadContext: (subKeypad: SecureContext.SubKeypadContext) =>
            set((state) => ({ context: { ...state.context, subKeypad: subKeypad } })),
        resetSubKeypadContext: () =>
            set((state) => {
                console.log(
                    '### state.context.subKeypad?.extE2EPlainMap : ',
                    state.context.subKeypad?.extE2EPlainMap,
                );
                return {
                    context: omitDeepUndefined({
                        ...state.context,
                        subKeypad: { extE2EPlainMap: state.context.subKeypad?.extE2EPlainMap },
                    }),
                };
            }),
        resetKeypadPlainMapSecureContext: () =>
            set((state) => {
                return {
                    context: omitDeepUndefined({
                        ...state.context,
                        keypad: { ...(state.context.keypad ?? {}), extE2EPlainMap: {} },
                        subKeypad: { ...(state.context.subKeypad ?? {}), extE2EPlainMap: {} },
                    }),
                };
            }),
    }));

const useSecureContextStore = (() => {
    if (_.isEmpty(storeInstance)) {
        storeInstance = createStore();
    }

    return storeInstance!;
})();

export { useSecureContextStore };
